import styled from 'styled-components';
import { FormikErrors, FormikTouched, FormikValues } from 'formik';

import { FitFeedbackFormActions } from 'productTemplate/state/fitFeedbackFormReducer';
import { QuestionForm } from 'shared/components/QuestionForm';
import { SNEAKERS_FIT as SNEAKERS_FIT_DATA } from 'productTemplate/enums/fitFeedback';
import { transformSizeOptions } from 'productTemplate/utils/transformSizeOptionsForFitFeedbackForm';
import {
  useFitFeedbackFormDispatch,
  useFitFeedbackFormState,
} from 'productTemplate/providers/FitFeedbackProvider';
import { useStoreSelector } from 'store/hooks/storeHooks';
import { QuestionFormType } from 'shared/enums/QuestionFormType';
import { t } from 'localization/utils/t';
import { getTranslations } from 'shared/utils/getTranslations';

const TP = 'productTemplate.fitFeedback.FitFeedbackFormSneakers';

const gridStyle = {
  display: 'grid',
  gridGap: '16px',
  gridTemplateColumns: 'repeat(6, 1fr)',
};

interface IFitFeedbackFormSneakers {
  errors: FormikErrors<{ [key: string]: string }>;
  setFieldValue: (key: string, val: string) => void;
  touched: FormikTouched<FormikValues>;
}

export const FitFeedbackFormSneakers = ({
  errors,
  setFieldValue,
  touched,
}: IFitFeedbackFormSneakers) => {
  const { sizeOptions } = useStoreSelector((state) => state.pdp.productTemplate);
  const transformedSizeOptions = transformSizeOptions(sizeOptions);
  const dispatch = useFitFeedbackFormDispatch();
  const sizePurchasedError = touched?.sneakersSizePurchased && errors?.sneakersSizePurchased;
  const sneakersFitError = touched?.sneakersFit && errors?.sneakersFit;
  const { sneakersSizePurchased, sneakersFit } = useFitFeedbackFormState();
  const SNEAKERS_FIT = getTranslations(SNEAKERS_FIT_DATA);
  const handleOnChange = (type) => (e) => {
    const { value, name } = e.target;
    dispatch({ type, payload: value });
    setFieldValue(name, value);
  };

  return (
    <FitFeedbackFormSneakers.Wrapper>
      <QuestionForm
        checked={sneakersSizePurchased}
        data-qa="fit_feedback"
        error={sizePurchasedError}
        gridStyle={gridStyle}
        name="sneakersSizePurchased"
        onChange={handleOnChange(FitFeedbackFormActions.setSneakersSizePurchased)}
        options={transformedSizeOptions}
        question={t(`${TP}.sneakersSizePurchased`, 'What size shoe did you purchase?')}
      />
      <FitFeedbackFormSneakers.HorizontalLine />
      <FitFeedbackFormSneakers.QuestionForm
        checked={sneakersFit}
        data-qa="fit_feedback"
        error={sneakersFitError}
        name="sneakersFit"
        onChange={handleOnChange(FitFeedbackFormActions.setSneakersFit)}
        options={SNEAKERS_FIT}
        question={t(`${TP}.sneakersFit`, 'How did the shoe end up fitting?')}
        type={QuestionFormType.CHECKBOX_RADIO}
      />
    </FitFeedbackFormSneakers.Wrapper>
  );
};

FitFeedbackFormSneakers.Wrapper = styled.div`
  margin-top: 4px;
`;

FitFeedbackFormSneakers.HorizontalLine = styled.hr`
  margin: 0 -16px;
`;

FitFeedbackFormSneakers.QuestionForm = styled(QuestionForm)`
  margin-top: 16px;

  ${QuestionForm.RadioOptions} {
    flex-direction: column;
    gap: 8px;
    align-items: flex-start;
  }
`;
